import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useLang } from "@/contexts/LanguageContext";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Send } from "lucide-react";

const Contact = () => {
  const { t } = useLang();
  const { content } = useSiteContent();
  const cms = content?.contact;

  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(t.contact.sent, { description: t.contact.sent_desc });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(t.common.error);
    } finally {
      setLoading(false);
    }
  };

  // Effective values: CMS first, fallback to i18n
  const address = cms?.address || t.contact.address;
  const phones = cms?.phones?.length
    ? cms.phones
    : [{ label: "Phone", number: t.contact.phone }];
  const emails = cms?.emails?.length
    ? cms.emails
    : [{ label: "Email", address: t.contact.mail }];

  return (
    <section id="contact" className="py-20 md:py-28 bg-[#FDFBF7]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-12 grid md:grid-cols-2 gap-12 lg:gap-16">
        <div>
          <p className="text-overline mb-3">{t.contact.overline}</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#1E3A5F] leading-tight mb-4">{t.contact.title}</h2>
          <p className="text-[#635F59] leading-relaxed mb-10 max-w-md">{t.contact.subtitle}</p>

          <div className="flex flex-col gap-5">
            <div className="flex items-start gap-4" data-testid="contact-address-row">
              <div className="w-10 h-10 rounded-full bg-[#AC5D45]/10 flex items-center justify-center shrink-0">
                <MapPin className="w-4 h-4 text-[#AC5D45]" />
              </div>
              <div>
                <p className="text-xs text-[#635F59] uppercase tracking-wider mb-1">Address</p>
                <p className="text-[#2D2926] font-medium text-sm leading-snug">{address}</p>
              </div>
            </div>

            {phones.map((p, i) => (
              <div key={`ph-${i}`} className="flex items-start gap-4" data-testid={`contact-phone-row-${i}`}>
                <div className="w-10 h-10 rounded-full bg-[#AC5D45]/10 flex items-center justify-center shrink-0">
                  <Phone className="w-4 h-4 text-[#AC5D45]" />
                </div>
                <div>
                  <p className="text-xs text-[#635F59] uppercase tracking-wider mb-1">{p.label || "Phone"}</p>
                  <a href={`tel:${(p.number || "").replace(/\s+/g, "")}`} className="text-[#2D2926] font-medium hover:text-[#AC5D45] transition">
                    {p.number}
                  </a>
                </div>
              </div>
            ))}

            {emails.map((m, i) => (
              <div key={`em-${i}`} className="flex items-start gap-4" data-testid={`contact-email-row-${i}`}>
                <div className="w-10 h-10 rounded-full bg-[#AC5D45]/10 flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4 text-[#AC5D45]" />
                </div>
                <div>
                  <p className="text-xs text-[#635F59] uppercase tracking-wider mb-1">{m.label || "Email"}</p>
                  <a href={`mailto:${m.address}`} className="text-[#2D2926] font-medium hover:text-[#AC5D45] transition">
                    {m.address}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white border border-[#E8E2D9] rounded-2xl p-7 md:p-9 shadow-sm"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <Label htmlFor="c-name">{t.contact.name}</Label>
              <Input id="c-name" required data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="c-email">{t.contact.email}</Label>
              <Input id="c-email" type="email" required data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
            </div>
          </div>
          <div className="mb-4">
            <Label htmlFor="c-subj">{t.contact.subject}</Label>
            <Input id="c-subj" required data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5" />
          </div>
          <div className="mb-5">
            <Label htmlFor="c-msg">{t.contact.message}</Label>
            <Textarea id="c-msg" required data-testid="contact-message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="w-full bg-[#AC5D45] hover:bg-[#8A4936] text-white rounded-full h-11"
          >
            <Send className="w-4 h-4 mr-2" />
            {loading ? t.common.loading : t.contact.send}
          </Button>
        </form>
      </div>
    </section>
  );
};

export default Contact;
